/**
 * Audio Device Test Utility
 *
 * Tests microphone and speaker access, device enumeration,
 * and setSinkId functionality to diagnose audio device issues.
 */

import { SELECTED_SPEAKER } from 'Constants/localstorage';
import localforage from 'localforage';

interface MicrophoneAccessResult {
  success: boolean;
  deviceCount?: number;
  devices?: {
    label: string;
    id: string;
    enabled: boolean;
    muted: boolean;
    readyState: string;
    settings: MediaTrackSettings;
  }[];
  error?: string;
  message?: string;
  details?: {
    name: string;
    message: string;
    constraint?: string;
  };
}

interface EnumerateDevicesResult {
  success: boolean;
  totalDevices?: number;
  audioInputs?: {
    deviceId: string;
    label: string;
    groupId?: string;
  }[];
  audioOutputs?: {
    deviceId: string;
    label: string;
    groupId?: string;
  }[];
  hasLabels?: boolean;
  error?: string;
  message?: string;
}

interface SetSinkIdResult {
  success: boolean;
  supported: boolean;
  error?: string;
  message?: string;
  browser?: string;
  deviceId?: string;
  sinkId?: string;
  details?: {
    name: string;
    message: string;
    code?: string;
  };
}

interface ComprehensiveTestResults {
  timestamp: string;
  browser: {
    userAgent: string;
    platform: string;
  };
  tests: {
    apiAvailability?: {
      name: string;
      available: boolean;
      getUserMediaSupported: boolean;
      enumerateDevicesSupported: boolean;
    };
    microphoneAccess?: MicrophoneAccessResult;
    deviceEnumeration?: EnumerateDevicesResult;
    setSinkIdTest?: SetSinkIdResult;
    summary?: {
      success: boolean;
      microphoneAccess: boolean;
      deviceEnumeration: boolean;
      setSinkIdSupported: boolean;
      audioInputCount: number;
      audioOutputCount: number;
      criticalErrors: {
        test: string;
        error: string;
        message: string;
      }[];
    };
  };
  summary?: {
    success: boolean;
    microphoneAccess?: boolean;
    deviceEnumeration?: boolean;
    setSinkIdSupported?: boolean;
    audioInputCount?: number;
    audioOutputCount?: number;
    criticalErrors?: {
      test: string;
      error: string;
      message: string;
    }[];
  };
}

/**
 * Test microphone access and permissions
 * @returns {Promise<MicrophoneAccessResult>} Test results with mic info or error
 */
async function testMicrophoneAccess(): Promise<MicrophoneAccessResult> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const tracks = stream.getAudioTracks();
    const results: MicrophoneAccessResult = {
      success: true,
      deviceCount: tracks.length,
      devices: tracks.map((track) => ({
        label: track.label || 'Unknown Device',
        id: track.id,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
        settings: track.getSettings ? track.getSettings() : {},
      })),
    };

    // Clean up - stop all tracks
    tracks.forEach((track) => track.stop());

    return results;
  } catch (error) {
    const errorObj = error as DOMException;

    return {
      success: false,
      error: errorObj.name,
      message: errorObj.message,
      details: {
        name: errorObj.name,
        message: errorObj.message,
        constraint: (error as any).constraint,
      },
    };
  }
}

/**
 * Enumerate all available media devices
 * @returns {Promise<EnumerateDevicesResult>} Categorized device lists
 */
async function enumerateAudioDevices(): Promise<EnumerateDevicesResult> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const audioInputs = devices.filter((d) => d.kind === 'audioinput');
    const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');

    return {
      success: true,
      totalDevices: devices.length,
      audioInputs: audioInputs.map((d) => ({
        deviceId: d.deviceId,
        label: d.label || 'Unnamed Device',
        groupId: d.groupId,
      })),
      audioOutputs: audioOutputs.map((d) => ({
        deviceId: d.deviceId,
        label: d.label || 'Unnamed Device',
        groupId: d.groupId,
      })),
      hasLabels:
        audioInputs.some((d) => d.label) || audioOutputs.some((d) => d.label),
    };
  } catch (error) {
    const errorObj = error as Error;

    return {
      success: false,
      error: errorObj.name,
      message: errorObj.message,
    };
  }
}

/**
 * Test setSinkId functionality (speaker selection)
 * @returns {Promise<SetSinkIdResult>} Test results
 */
async function testSetSinkId(): Promise<SetSinkIdResult> {
  const outputDevices = (
    await navigator.mediaDevices.enumerateDevices()
  ).filter((d) => d.kind === 'audiooutput');

  let deviceId = await localforage.getItem<string>(SELECTED_SPEAKER);

  if (!deviceId || !outputDevices.some((d) => d.deviceId === deviceId)) {
    deviceId =
      outputDevices.find((d) => d.label === 'default')?.deviceId ||
      outputDevices[0]?.deviceId;
  }

  try {
    // Create a test audio element
    const audio = new Audio();

    // Check if setSinkId is supported
    if (typeof audio.setSinkId !== 'function') {
      return {
        success: false,
        supported: false,
        error: 'setSinkId not supported',
        message: 'Browser does not support audio output device selection',
        browser: navigator.userAgent,
      };
    }

    // Try to set the sink ID
    await audio.setSinkId(deviceId);

    return {
      success: true,
      supported: true,
      deviceId: deviceId,
      sinkId: audio.sinkId,
    };
  } catch (error) {
    const errorObj = error as DOMException;

    return {
      success: false,
      supported: true,
      error: errorObj.name,
      message: errorObj.message,
      deviceId: deviceId,
      details: {
        name: errorObj.name,
        message: errorObj.message,
        code: (error as any).code,
      },
    };
  }
}

/**
 * Comprehensive audio device test
 * Tests all audio functionality and returns detailed diagnostics
 * @returns {Promise<ComprehensiveTestResults>} Complete test results
 */
async function comprehensiveAudioDeviceTest(): Promise<ComprehensiveTestResults> {
  const results: ComprehensiveTestResults = {
    timestamp: new Date().toISOString(),
    browser: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
    },
    tests: {},
  };

  // Test 1: Check API availability
  results.tests.apiAvailability = {
    name: 'MediaDevices API',
    available: !!navigator.mediaDevices,
    getUserMediaSupported: !!(
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    ),
    enumerateDevicesSupported: !!(
      navigator.mediaDevices && navigator.mediaDevices.enumerateDevices
    ),
  };

  if (!results.tests.apiAvailability.available) {
    results.summary = {
      success: false,
      criticalErrors: [
        {
          test: 'MediaDevices API',
          error: 'MediaDevices API not available',
          message: 'MediaDevices API not available',
        },
      ],
    };
    return results;
  }

  // Test 2: Microphone Access
  results.tests.microphoneAccess = await testMicrophoneAccess();

  // Test 3: Device Enumeration
  results.tests.deviceEnumeration = await enumerateAudioDevices();

  // Test 4: setSinkId Support
  results.tests.setSinkIdTest = await testSetSinkId();

  // Generate summary
  const micSuccess = results.tests.microphoneAccess.success;
  const enumSuccess = results.tests.deviceEnumeration.success;
  const sinkIdSupported = results.tests.setSinkIdTest.supported;

  results.summary = {
    success: micSuccess && enumSuccess,
    microphoneAccess: micSuccess,
    deviceEnumeration: enumSuccess,
    setSinkIdSupported: sinkIdSupported,
    audioInputCount: enumSuccess
      ? results.tests.deviceEnumeration.audioInputs.length
      : 0,
    audioOutputCount: enumSuccess
      ? results.tests.deviceEnumeration.audioOutputs.length
      : 0,
    criticalErrors: [],
  };

  // Collect critical errors
  if (!micSuccess) {
    results.summary.criticalErrors.push({
      test: 'Microphone Access',
      error: results.tests.microphoneAccess.error,
      message: results.tests.microphoneAccess.message,
    });
  }

  if (!enumSuccess) {
    results.summary.criticalErrors.push({
      test: 'Device Enumeration',
      error: results.tests.deviceEnumeration.error,
      message: results.tests.deviceEnumeration.message,
    });
  }

  if (!sinkIdSupported) {
    results.summary.criticalErrors.push({
      test: 'Speaker Selection',
      error: results.tests.setSinkIdTest.error,
      message: results.tests.setSinkIdTest.message,
    });
  }

  return results;
}

function formatAudioDeviceTestResults(
  testResults: ComprehensiveTestResults
): string {
  const timestamp = testResults.timestamp;
  let output = `Audio Device Test Results\n`;
  output += `Timestamp: ${timestamp}\n`;
  output += `=`.repeat(70) + '\n\n';

  // Browser Info
  output += `Browser Information:\n`;
  output += `  User Agent: ${testResults.browser.userAgent}\n`;
  output += `  Platform: ${testResults.browser.platform}\n\n`;

  // Summary
  output += `Summary:\n`;
  output += `  Overall Status: ${
    testResults.summary.success ? '[SUCCESS ✓]' : '[FAILED ✗]'
  }\n`;
  output += `  Microphone Access: ${
    testResults.summary.microphoneAccess ? '[OK ✓]' : '[FAILED ✗]'
  }\n`;
  output += `  Device Enumeration: ${
    testResults.summary.deviceEnumeration ? '[OK ✓]' : '[FAILED ✗]'
  }\n`;
  output += `  setSinkId Support: ${
    testResults.summary.setSinkIdSupported ? '[OK ✓]' : '[NOT SUPPORTED ✗]'
  }\n`;
  output += `  Audio Inputs Found: ${testResults.summary.audioInputCount}\n`;
  output += `  Audio Outputs Found: ${testResults.summary.audioOutputCount}\n\n`;

  // Critical Errors
  if (testResults.summary.criticalErrors.length > 0) {
    output += `Critical Errors:\n`;
    testResults.summary.criticalErrors.forEach((err: any, index: number) => {
      output += `  ${index + 1}. ${err.test}: ${err.error}\n`;
      output += `     Message: ${err.message}\n`;
    });
    output += `\n`;
  }

  output += `Detailed Test Results:\n`;
  output += `-`.repeat(70) + '\n\n';

  // API Availability
  output += `1. MediaDevices API Availability\n`;
  output += `   API Available: ${
    testResults.tests.apiAvailability.available ? '✓' : '✗'
  }\n`;
  output += `   getUserMedia Supported: ${
    testResults.tests.apiAvailability.getUserMediaSupported ? '✓' : '✗'
  }\n`;
  output += `   enumerateDevices Supported: ${
    testResults.tests.apiAvailability.enumerateDevicesSupported ? '✓' : '✗'
  }\n\n`;

  // Microphone Access
  output += `2. Microphone Access Test\n`;
  if (testResults.tests.microphoneAccess.success) {
    output += `   Status: [SUCCESS ✓]\n`;
    output += `   Devices Found: ${testResults.tests.microphoneAccess.deviceCount}\n`;
    testResults.tests.microphoneAccess.devices.forEach(
      (dev: any, i: number) => {
        output += `   Device ${i + 1}:\n`;
        output += `     Label: ${dev.label}\n`;
        output += `     ID: ${dev.id}\n`;
        output += `     Enabled: ${dev.enabled}\n`;
        output += `     Ready State: ${dev.readyState}\n`;
      }
    );
  } else {
    output += `   Status: [FAILED ✗]\n`;
    output += `   Error: ${testResults.tests.microphoneAccess.error}\n`;
    output += `   Message: ${testResults.tests.microphoneAccess.message}\n`;
    if (testResults.tests.microphoneAccess.details) {
      output += `   Details: ${JSON.stringify(
        testResults.tests.microphoneAccess.details,
        null,
        2
      )}\n`;
    }
  }
  output += `\n`;

  // Device Enumeration
  output += `3. Device Enumeration Test\n`;
  if (testResults.tests.deviceEnumeration.success) {
    output += `   Status: [SUCCESS ✓]\n`;
    output += `   Total Devices: ${testResults.tests.deviceEnumeration.totalDevices}\n`;
    output += `   Has Labels: ${
      testResults.tests.deviceEnumeration.hasLabels ? 'Yes' : 'No'
    }\n\n`;

    output += `   Audio Inputs (${testResults.tests.deviceEnumeration.audioInputs.length}):\n`;
    testResults.tests.deviceEnumeration.audioInputs.forEach(
      (dev: any, i: number) => {
        output += `     ${i + 1}. ${dev.label}\n`;
        output += `        ID: ${dev.deviceId}\n`;
      }
    );

    output += `\n   Audio Outputs (${testResults.tests.deviceEnumeration.audioOutputs.length}):\n`;
    testResults.tests.deviceEnumeration.audioOutputs.forEach(
      (dev: any, i: number) => {
        output += `     ${i + 1}. ${dev.label}\n`;
        output += `        ID: ${dev.deviceId}\n`;
      }
    );
  } else {
    output += `   Status: [FAILED ✗]\n`;
    output += `   Error: ${testResults.tests.deviceEnumeration.error}\n`;
    output += `   Message: ${testResults.tests.deviceEnumeration.message}\n`;
  }
  output += `\n`;

  // setSinkId Test
  output += `4. setSinkId (Speaker Selection) Test\n`;
  if (testResults.tests.setSinkIdTest.supported) {
    output += `   Supported: [YES ✓]\n`;
    if (testResults.tests.setSinkIdTest.success) {
      output += `   Selected Device Test: [SUCCESS ✓]\n`;
      output += `   Sink ID: ${testResults.tests.setSinkIdTest.sinkId}\n`;
    } else {
      output += `   Selected Device Test: [FAILED ✗]\n`;
      output += `   Error: ${testResults.tests.setSinkIdTest.error}\n`;
      output += `   Message: ${testResults.tests.setSinkIdTest.message}\n`;
    }
  } else {
    output += `   Supported: [NO ✗]\n`;
    output += `   Message: ${testResults.tests.setSinkIdTest.message}\n`;
  }
  output += `\n`;

  output += `=`.repeat(70) + '\n';
  output += `\nInterpretation & Recommendations:\n`;

  if (testResults.summary.success) {
    output += `✓ All audio device tests passed successfully.\n`;
    output += `  Your audio devices are working correctly.\n`;
    output += `  Microphone and speaker selection should work for VoIP calls.\n`;
  } else {
    output += `✗ Some audio device tests failed. Common issues:\n\n`;

    if (!testResults.summary.microphoneAccess) {
      output += `  MICROPHONE ACCESS DENIED:\n`;
      output += `  • Check browser permissions (camera/microphone)\n`;
      output += `  • Click the lock icon in the address bar\n`;
      output += `  • Reload the page after granting permissions\n`;
      output += `  • Check system privacy settings\n\n`;
    }

    if (!testResults.summary.deviceEnumeration) {
      output += `  DEVICE ENUMERATION FAILED:\n`;
      output += `  • No audio devices detected\n`;
      output += `  • Check if microphone/speakers are connected\n`;
      output += `  • Restart browser\n\n`;
    }

    if (!testResults.summary.setSinkIdSupported) {
      output += `  SPEAKER SELECTION NOT SUPPORTED:\n`;
      output += `  • Browser does not support setSinkId\n`;
      output += `  • Use Chrome, Edge, or Opera\n`;
      output += `  • System default speaker will be used\n\n`;
    }

    output += `  Recommended actions:\n`;
    output += `  1. Grant microphone permissions in browser settings\n`;
    output += `  2. Check system audio settings\n`;
    output += `  3. Try a different browser (Chrome recommended)\n`;
    output += `  4. Check if other apps can access audio devices\n`;
    output += `  5. Contact support with this test log\n`;
  }

  return output;
}

// Export for ES modules (React/TypeScript)
export {
  testMicrophoneAccess,
  enumerateAudioDevices,
  testSetSinkId,
  comprehensiveAudioDeviceTest,
  formatAudioDeviceTestResults,
};
